import { memo } from 'react';
import { Handle, Position, NodeProps } from '@xyflow/react';
import classNames from 'classnames';

import { Icon } from '@/components/icon';
import { getIconForFieldType } from '@/explore/model/utils';
import type { Model } from '@/explore/types';
import { Tooltip } from '@/components/tooltip';

import styles from './models.module.scss';

export interface ModelNodeData extends Record<string, unknown> {
  model: Model;
  highlightedPropertyKey?: string;
  onPropertyHover?: (propertyKey: string | null) => void;
}

const ModelTypeColors: Record<string, string> = {
  Account: '#8B5CF6',
  User: '#3B82F6',
  Event: '#10B981',
  Entity: '#F59E0B',
};

const VisiblePropertiesLimit = 10;

const ModelNode = ({ data, selected }: NodeProps) => {
  const { model, highlightedPropertyKey, onPropertyHover } = data as ModelNodeData;
  const modelKind = model.semantics && 'kind' in model.semantics ? model.semantics.kind : null;
  const kindColor = modelKind ? ModelTypeColors[modelKind] || '#6B7280' : '#6B7280';

  const propertyLineHeight = 24;

  const visibleProperties = model.properties.slice(0, VisiblePropertiesLimit);
  const hasMore = model.properties.length > VisiblePropertiesLimit;

  return (
    <>
      <Handle type="source" hidden position={Position.Right} id="right" />
      <Handle type="target" hidden position={Position.Left} id="left" />

      <div
        className={classNames(styles.modelNode, { [styles.selected]: selected })}
        style={{ cursor: 'grab' }}>
        <div
          className={styles.nodeHeader}
          style={{
            backgroundColor: `${kindColor}1A`,
            borderBottom: `1px solid ${kindColor}`,
          }}>
          <Icon name="Model" size={20} color={kindColor} />
          <span className={styles.nodeName} title={model.name}>
            {model.name}
          </span>
          {modelKind && (
            <Tooltip content={`Tagged as a semantic ${modelKind} model`}>
              <span className={styles.nodeKind}>{modelKind}</span>
            </Tooltip>
          )}
        </div>

        <div className={styles.properties}>
          {visibleProperties.map((prop) => {
            const isPrimaryKey = model.primaryKey.includes(prop.key);
            const isHighlighted = highlightedPropertyKey === prop.key;
            const icon = getIconForFieldType(prop.type);

            return (
              <div
                key={prop.key}
                className={classNames(styles.property, { [styles.active]: isHighlighted })}
                onMouseEnter={() => onPropertyHover?.(prop.key)}
                onMouseLeave={() => onPropertyHover?.(null)}
                style={{ height: `${propertyLineHeight}px` }}>
                <div className={styles.propertyIcon}>
                  <Icon name={icon} size={14} />
                  {isPrimaryKey && <Icon name="Key" size={10} className={styles.primaryKeyIcon} />}
                </div>
                <span className={styles.propertyName} title={prop.name}>
                  {prop.name}
                </span>
                <span className={styles.propertyType}>{prop.type}</span>
              </div>
            );
          })}

          {/* Show "more" indicator if properties are truncated */}
          {hasMore && (
            <div className={styles.moreIndicator}>
              {`+${model.properties.length - VisiblePropertiesLimit} more...`}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default memo(ModelNode);
